import React from 'react'

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { Provider as ReduxProvider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'

import { SnackbarProvider } from 'notistack'
import { SuspenseRoot } from '../Suspense'
import { Store } from 'redux'
import { Persistor } from 'redux-persist'
import { LocationProvider } from '../Provider/LocationProvider'
import QueryProvider from '../Provider/QueryProvider'
import { ThemeProvider } from '../Provider'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import i18next from 'i18next'
import { CookiesProvider } from 'react-cookie'
import ErrorBoundary from '../Error/ErrorBoundary'
import { makeStyles } from 'tss-react/mui'
import Snackbar from '../Snackbar/Snackbar'
import { J2Caution, J2Validate, K1Information } from '@e3dc-react/icons'
import { useDatePickerLanguages } from '../../Hooks'
import { AuthProvider } from '../../Libs/authentication'
interface StyleProps {
  isWebView: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  snackbar: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: props.isWebView ? 0 : MARGIN_WITH_BOTTOM_BAR,
    },
  },
}))

export interface AppShellProps {
  store: Store
  persistor: Persistor
  errorComponent: React.ReactNode
  children?: React.ReactNode
}

export const muiCache = createCache({
  key: 'mui',
  prepend: true,
})

const WEBVIEW_REGEX = new RegExp('iPhone|iPad|Android')
const MARGIN_WITH_BOTTOM_BAR = 42

const AppShell: React.FC<AppShellProps> = ({ store, persistor, children, errorComponent }): React.ReactElement => {
  const isWebView = WEBVIEW_REGEX.test(navigator.userAgent)
  const { classes } = useStyles({ isWebView })
  const localeText = useDatePickerLanguages()

  return (
    <AuthProvider>
      <CacheProvider value={muiCache}>
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={i18next.language} localeText={localeText}>
          <ReduxProvider store={store}>
            <ThemeProvider>
              <SnackbarProvider
                maxSnack={3}
                preventDuplicate={true}
                classes={{ containerRoot: classes.snackbar }}
                Components={{
                  success: Snackbar,
                  error: Snackbar,
                  warning: Snackbar,
                  info: Snackbar,
                }}
                iconVariant={{
                  success: <J2Validate />,
                  warning: <J2Caution />,
                  error: <J2Caution />,
                  info: <K1Information />,
                }}
              >
                {/* if a app doesnt use persistor, we gotta throw this in conditionally... at that point we might wanna define the redux provider+perist gate and whatever in the app itself and wrap children in it here*/}
                <PersistGate persistor={persistor}>
                  <QueryProvider>
                    <SuspenseRoot>
                      <ErrorBoundary errorComponent={errorComponent}>
                        <LocationProvider>
                          <CookiesProvider>{children}</CookiesProvider>
                        </LocationProvider>
                      </ErrorBoundary>
                    </SuspenseRoot>
                  </QueryProvider>
                </PersistGate>
              </SnackbarProvider>
            </ThemeProvider>
          </ReduxProvider>
        </LocalizationProvider>
      </CacheProvider>
    </AuthProvider>
  )
}

export default AppShell
